import { type ChangeEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { debounce } from 'lodash';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { type EntityType } from '@/types/entity';
import { useRouter } from 'next/router';

export const useSearchQuery = (tableEntity: EntityType) => {
  const { query, isReady } = useRouter();
  const setPageIndex = useTableStore(useShallow((state) => state.setPageIndex));

  const queryString = useTableStore((s) => s.queryStrings?.[tableEntity] ?? '');
  const setQueryString = useTableStore((s) => s.setQueryString);
  // Store-backed (not local state): this hook runs in more than one component for the same
  // entity (home + gallery layout), and closing the filter from one must close them all.
  const showFilterInput = useTableStore((s) => s.showFilterInputs?.[tableEntity] ?? false);
  const setShowFilterInput = useTableStore((s) => s.setShowFilterInput);
  const setLocateAssetId = useBrowserStore((s) => s.setLocateAssetId);
  const seededFromUrl = useRef(false);

  const handleSearchOnChange = useCallback(
    (qs: string) => {
      setLocateAssetId(undefined);
      setQueryString(tableEntity, qs);
      setPageIndex(tableEntity, 0);
    },
    [tableEntity, setQueryString, setLocateAssetId, setPageIndex],
  );

  // Stable debouncer: recreating it on every render would let any unrelated
  // re-render cancel a pending search via the cleanup effect below.
  const debouncedSearch = useMemo(
    () =>
      debounce((e: ChangeEvent<HTMLInputElement>) => {
        handleSearchOnChange(e.target.value);
      }, 300),
    [handleSearchOnChange],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // When the filter closes, drop any keystroke still in the debounce window — otherwise it
  // commits ~300ms after the bar is hidden and the view stays filtered invisibly. Runs on the
  // shared store flag so it also covers a close triggered by another instance of this hook.
  useEffect(() => {
    if (!showFilterInput) {
      debouncedSearch.cancel();
    }
  }, [showFilterInput, debouncedSearch]);

  // Reset the filter when leaving the view (or switching entity) so each visit starts blank.
  useEffect(() => {
    return () => {
      setQueryString?.(tableEntity, '');
      setShowFilterInput?.(tableEntity, false);
    };
  }, [tableEntity, setQueryString, setShowFilterInput]);

  const setShowFilter = useCallback(
    (showFilter: boolean) => {
      setShowFilterInput(tableEntity, showFilter);

      // Clear search if filter is closed
      if (!showFilter) {
        setQueryString(tableEntity, '');
      }
    },
    [tableEntity, setQueryString, setShowFilterInput],
  );

  // Seed the filter from the URL (deep links) exactly once the router is ready —
  // the Pages Router leaves `query` empty until `isReady`, so a mount-only effect
  // misses cold loads. The store stays the source of truth afterward: the ref
  // guard means we never re-seed over later edits or a cleared filter.
  useEffect(() => {
    if (!isReady || seededFromUrl.current) {
      return;
    }
    seededFromUrl.current = true;

    const urlQueryString = (query.queryString as string) ?? '';
    if (urlQueryString && !queryString) {
      setQueryString(tableEntity, urlQueryString);
    }
    if (urlQueryString || queryString) {
      setShowFilterInput(tableEntity, true);
    }
  }, [isReady, query.queryString, queryString, tableEntity, setQueryString, setShowFilterInput]);

  return { queryString, debouncedSearch, showFilterInput, setShowFilter };
};
